/**
 * @scope console
 *
 * ContentBlock — flat "header band + body" primitive that lives
 * directly on the page surface. Pairs with `RaisedCard` (the
 * raised-surface counterpart for self-contained data
 * presentations).
 *
 * Use when content groups under a heading and visually integrates
 * with the surrounding page (no background, no card outline). When
 * stacked inside a `flex flex-col gap-N` column, the optional
 * `divider` prop draws a top border so consecutive blocks are
 * separated without nested cards.
 *
 * Header anatomy: h2 title + optional small description + optional
 * right-side action slot. With `actions` set, the header switches
 * to a flex-between row so the action sits baseline-aligned with
 * the title.
 */

import type React from "react";

import { cn } from "../../utils/cn";
import { Heading } from "../ui/heading";

export interface ContentBlockProps {
    /**
     * Block title — h2 typography. ReactNode so callers can compose
     * richer titles (icon + text, title + count chip, etc.).
     */
    title: React.ReactNode;

    /**
     * Optional small-text description rendered directly under the
     * title within the header. Muted foreground color.
     */
    description?: React.ReactNode;

    /**
     * Optional right-side action slot — a button, action menu, or
     * cluster of controls. Aligned to the title's baseline. When set,
     * the header switches to a flex-between row.
     */
    actions?: React.ReactNode;

    /**
     * Draw a 1px top divider above the block. Use when stacking
     * multiple blocks in a column to separate each one. Default
     * `false` (the first block in a column typically doesn't need
     * one).
     */
    divider?: boolean;

    children: React.ReactNode;
    className?: string;
}

export function ContentBlock({
    title,
    description,
    actions,
    divider = false,
    children,
    className
}: ContentBlockProps): React.ReactElement {
    return (
        <section
            className={cn(
                "flex flex-col gap-3",
                divider && "border-t border-line-base pt-4",
                className
            )}
        >
            <Heading
                asElement="header"
                title={title}
                titleVariant="h2"
                subtitle={description}
                actions={actions}
                className="gap-0.5"
            />
            {children}
        </section>
    );
}
